import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Inbox, Search, Phone, Mail, Car, Download, Loader2, CalendarDays } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPO = { privato: "Privato", "partita-iva": "Partita IVA", azienda: "Azienda" };

const dataIt = (iso) =>
  new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const esportaCsv = (leads) => {
  const righe = [
    ["Data", "Nome e cognome", "Telefono", "Email", "Tipologia", "Auto richiesta", "Pagina", "Sorgente"],
    ...leads.map((l) => [dataIt(l.created_at), l.nome, l.telefono, l.email, TIPO[l.tipo_utente] || l.tipo_utente, l.auto_budget, l.pagina, l.utm_source]),
  ];
  const blob = new Blob(["\uFEFF" + righe.map((r) => r.map(csvCell).join(";")).join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `lead-xmove-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

export const LeadsRendiconto = () => {
  const [leads, setLeads] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/leads`).then((r) => setLeads(r.data)).catch(() => setLeads([]));
  }, []);

  const filtrati = useMemo(() => {
    if (!leads) return [];
    const t = q.trim().toLowerCase();
    if (!t) return leads;
    return leads.filter((l) => `${l.nome} ${l.telefono} ${l.email || ""} ${l.auto_budget}`.toLowerCase().includes(t));
  }, [leads, q]);

  const oggi = new Date().toDateString();
  const nOggi = (leads || []).filter((l) => new Date(l.created_at).toDateString() === oggi).length;
  const n7 = (leads || []).filter((l) => Date.now() - new Date(l.created_at) < 7 * 86400000).length;

  return (
    <section className="mt-14" data-testid="leads-section">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-xmove-orange">
        <Inbox size={14} /> Richieste ricevute
      </p>
      <h2 className="mt-2 font-display text-2xl font-bold text-xmove-ink sm:text-3xl">Chi ci ha contattato</h2>
      <p className="mt-2 max-w-2xl text-sm text-xmove-muted">
        Tutte le persone che hanno compilato il modulo preventivo sul sito, con data, auto richiesta e recapiti. Le più recenti in alto.
      </p>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-white px-3 py-1.5 text-xs font-bold text-xmove-ink shadow-sm" data-testid="leads-count">
          {leads ? leads.length : "…"} richieste totali
        </span>
        <span className="rounded-full bg-white px-3 py-1.5 text-xs font-bold text-emerald-600 shadow-sm" data-testid="leads-count-7d">{n7} ultimi 7 giorni</span>
        <span className="rounded-full bg-white px-3 py-1.5 text-xs font-bold text-xmove-orange shadow-sm" data-testid="leads-count-today">{nOggi} oggi</span>
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => esportaCsv(filtrati)}
          disabled={!filtrati.length}
          data-testid="leads-export-csv"
          className="flex items-center gap-1.5 rounded-lg border border-xmove-ink/15 bg-white px-3 py-2 text-xs font-bold text-xmove-ink transition-colors hover:border-xmove-orange hover:text-xmove-orange disabled:opacity-50"
        >
          <Download size={13} /> Esporta CSV
        </button>
      </div>

      <label className="relative mt-4 block">
        <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-xmove-muted" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca per nome, telefono, email o auto…"
          data-testid="leads-search-input"
          className="w-full rounded-xl border border-xmove-ink/10 bg-white py-3 pl-10 pr-4 text-sm text-xmove-ink outline-none transition-colors focus:border-xmove-orange"
        />
      </label>

      <div className="mt-4 overflow-hidden rounded-xl border border-xmove-ink/8 bg-white">
        {leads === null ? (
          <div className="flex items-center justify-center py-12" data-testid="leads-loading"><Loader2 className="animate-spin text-xmove-orange" size={22} /></div>
        ) : filtrati.length === 0 ? (
          <p className="py-12 text-center text-sm text-xmove-muted" data-testid="leads-empty">Nessuna richiesta {q ? "corrisponde alla ricerca" : "ricevuta finora"}.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm" data-testid="leads-table">
              <thead className="bg-xmove-cream text-[10px] font-bold uppercase tracking-wider text-xmove-muted">
                <tr>
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3">Nome e cognome</th>
                  <th className="px-4 py-3">Contatti</th>
                  <th className="px-4 py-3">Auto richiesta</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-xmove-ink/6">
                {filtrati.map((l) => (
                  <tr key={l.id} className="align-top transition-colors hover:bg-xmove-cream/60" data-testid={`lead-row-${l.id}`}>
                    <td className="whitespace-nowrap px-4 py-3 text-xmove-muted">
                      <span className="flex items-center gap-1.5"><CalendarDays size={13} className="text-xmove-orange" /> {dataIt(l.created_at)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-bold text-xmove-ink">{l.nome}</p>
                      <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-xmove-muted">{TIPO[l.tipo_utente] || l.tipo_utente}</p>
                    </td>
                    <td className="px-4 py-3">
                      <a href={`tel:${l.telefono}`} className="flex items-center gap-1.5 font-semibold text-xmove-ink hover:text-xmove-orange" data-testid={`lead-tel-${l.id}`}>
                        <Phone size={13} className="text-xmove-orange" /> {l.telefono}
                      </a>
                      {l.email ? (
                        <a href={`mailto:${l.email}`} className="mt-1 flex items-center gap-1.5 text-xmove-muted hover:text-xmove-orange" data-testid={`lead-email-${l.id}`}>
                          <Mail size={13} /> {l.email}
                        </a>
                      ) : (
                        <p className="mt-1 text-xs text-xmove-muted/70">email non indicata</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex items-start gap-1.5 text-xmove-ink"><Car size={14} className="mt-0.5 shrink-0 text-xmove-orange" /> {l.auto_budget}</span>
                      {l.pagina && <p className="mt-1 text-[11px] text-xmove-muted">da {l.pagina}{l.utm_source ? ` · ${l.utm_source}` : ""}</p>}
                      {l.partner && <p className="mt-1 inline-block rounded bg-xmove-night px-1.5 py-0.5 text-[10px] font-bold uppercase text-white" data-testid={`lead-partner-${l.id}`}>Partner {l.partner.rete}: {l.partner.officina} · {l.partner.email}</p>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};
